import { useTranslation } from 'react-i18next';
import { ListTotalsBar, sumByCurrency } from '../ui/list-totals-bar';
import { CopyNumberButton } from '../shared/CopyNumberButton';
import useRememberedInputs from '../../hooks/useRememberedInputs';
import { rangeToWeek } from '../../lib/weekFilter';
import { WeekRangeFilter } from '../ui/week-filter';
import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { SortableHeader, useSort, ResetViewButton } from '../ui/SortableHeader';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import PermButton from '../shared/PermButton';
import usePermissions from '../../hooks/usePermissions';
import { Checkbox } from '../ui/checkbox';
import { 
  Plus, 
  ListPlus, 
  Eye, 
  Send, 
  Trash2, 
  CheckCircle, 
  Download, 
  MoreHorizontal,
  XCircle,
  RotateCcw,
  DollarSign,
  Edit,
  Copy,
  Calendar,
  X,
  User,
  CheckSquare,
  Square,
  AlertTriangle,
  Upload,
  EyeOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { DateText } from '../ui/week-badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const ExpensesTable = ({
  expenses,
  projects,
  employees,
  expenseCategories,
  selectedProject,
  setSelectedProject,
  statusFilter,
  setStatusFilter,
  categoryFilter,
  setCategoryFilter,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  employeeFilter,
  setEmployeeFilter,
  isAdminOrManager,
  formatCurrency,
  getProjectName,
  getStatusBadge,
  onAddExpense,
  onBatchEntry,
  onViewReceipt,
  onDownloadReceipts,
  onEditExpense,
  onCopyExpense,
  onSubmitExpense,
  onDeleteExpense,
  onApproveExpense,
  onChangeStatus,
  onBulkAction,
  onExport,
  onReuploadReceipt,
  assetFilter,
  onClearAssetFilter,
  inventoryAssets,
  onSetAssetFilter
}) => {
  const { t } = useTranslation();
  const { can } = usePermissions();

  // Get unique employees from expenses for the filter
  const expenseEmployees = useMemo(() => {
    const uniqueIds = new Set();
    expenses.forEach(exp => {
      if (exp.assigned_employee_id) uniqueIds.add(exp.assigned_employee_id);
    });
    return employees?.filter(e => uniqueIds.has(e.id)) || [];
  }, [expenses, employees]);

  // Get unique categories from expenses for the filter
  const expenseUniqueCategories = useMemo(() => {
    const uniqueCats = new Set();
    expenses.forEach(exp => {
      if (exp.category) uniqueCats.add(exp.category);
    });
    return Array.from(uniqueCats);
  }, [expenses]);

  // Get unique payment methods from expenses for the filter
  const expenseUniquePaymentMethods = useMemo(() => {
    const uniq = new Set();
    expenses.forEach(exp => {
      if (exp.payment_method) uniq.add(exp.payment_method);
    });
    return Array.from(uniq).sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  // Payment-method filter (local state — keeps parent untouched)
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  useRememberedInputs('budget-expense-payment-filter', { paymentMethod: [paymentMethodFilter, setPaymentMethodFilter] },
    { skip: !!assetFilter, pause: !!assetFilter });

  // Filter the rows AFTER all parent filters but BEFORE rendering and totalling.
  const visibleExpenses = useMemo(() => {
    if (paymentMethodFilter === 'all') return expenses;
    if (paymentMethodFilter === '__none__') return expenses.filter(e => !e.payment_method);
    return expenses.filter(e => e.payment_method === paymentMethodFilter);
  }, [expenses, paymentMethodFilter]);

  // Dynamic PLN total of the currently visible rows. Falls back to original
  // amount for rows already in PLN that don't have amount_pln set.
  const visibleTotalPLN = useMemo(() => {
    return visibleExpenses.reduce((sum, e) => {
      if (e.amount_pln != null) return sum + Number(e.amount_pln);
      if ((e.currency || 'PLN').toUpperCase() === 'PLN') return sum + Number(e.amount || 0);
      return sum; // foreign-currency row without a stored PLN value — skip
    }, 0);
  }, [visibleExpenses]);

  // Multi-select state
  const [selectedExpenses, setSelectedExpenses] = useState(new Set());
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkComment, setBulkComment] = useState('');

  // State for status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [statusComment, setStatusComment] = useState('');
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('date', 'desc', 'budget-expenses');
  const handleSort = (key) => toggleSort(key, key === 'date' || key === 'created_at' ? 'desc' : 'asc');
  const isViewCustomized = sortKey !== 'date' || sortDir !== 'desc';

  // Helper to get display name for a category
  const getCategoryDisplayName = (categoryKey) => {
    if (!expenseCategories) return categoryKey;
    const cat = expenseCategories[categoryKey];
    if (!cat) return categoryKey;
    if (Array.isArray(cat)) return categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
    return cat.display_name || categoryKey;
  };

  // Open status change dialog
  const openStatusDialog = (expense, action) => {
    setSelectedExpense(expense);
    setStatusAction(action);
    setStatusComment('');
    setStatusDialogOpen(true);
  };

  // Handle status change confirmation
  const handleStatusChangeConfirm = () => {
    if (onChangeStatus && selectedExpense && statusAction) {
      onChangeStatus(selectedExpense.id, statusAction, statusComment);
    }
    setStatusDialogOpen(false);
    setSelectedExpense(null);
    setStatusAction(null);
    setStatusComment('');
  };

  // Get action label for dialog
  const getActionLabel = (action) => t(`budget.actionLabel.${action}`, { defaultValue: action });

  // Get action description for dialog
  const getActionDescription = (action) => {
    switch (action) {
      case 'revert_draft': return 'This will revert the expense back to draft status, removing any approvals. The expense amount will be deducted from budget calculations.';
      case 'reject': return 'This will reject the expense and notify the submitter. The expense amount will be deducted from budget calculations.';
      case 'approve': return 'This will approve the expense and include it in budget calculations.';
      case 'mark_paid': return 'This will mark the expense as paid/reimbursed.';
      default: return '';
    }
  };

  // Clear ALL filters in one go — the old button only reset the
  // date range; now it resets project / status / category / payment
  // method / employee / asset / date range simultaneously so a
  // single click gives a truly clean list.
  const clearAllFilters = () => {
    setSelectedProject('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setEmployeeFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    if (typeof onClearAssetFilter === 'function' && assetFilter) {
      onClearAssetFilter();
    }
  };

  // True whenever *any* filter is off its "all/empty" default. Drives
  // the visibility of the reset button so the toolbar stays clean
  // when nothing is filtered.
  const hasAnyActiveFilter = (
    (selectedProject && selectedProject !== 'all') ||
    (statusFilter && statusFilter !== 'all') ||
    (categoryFilter && categoryFilter !== 'all') ||
    (employeeFilter && employeeFilter !== 'all') ||
    (paymentMethodFilter && paymentMethodFilter !== 'all') ||
    !!dateFrom || !!dateTo || !!assetFilter
  );

  // Toggle single expense selection
  const toggleExpenseSelection = (expenseId) => {
    setSelectedExpenses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(expenseId)) {
        newSet.delete(expenseId);
      } else {
        newSet.add(expenseId);
      }
      return newSet;
    });
  };

  // Toggle all expenses selection
  const toggleAllExpenses = () => {
    if (selectedExpenses.size === expenses.length) {
      setSelectedExpenses(new Set());
    } else {
      setSelectedExpenses(new Set(expenses.map(e => e.id)));
    }
  };

  // Check if all expenses are selected
  const allSelected = expenses.length > 0 && selectedExpenses.size === expenses.length;
  const someSelected = selectedExpenses.size > 0 && selectedExpenses.size < expenses.length;

  // Get selected expenses data
  const getSelectedExpensesData = () => {
    return expenses.filter(e => selectedExpenses.has(e.id));
  };

  // Open bulk action dialog
  const openBulkActionDialog = (action) => {
    setBulkAction(action);
    setBulkComment('');
    setBulkActionDialogOpen(true);
  };

  // Handle bulk action confirmation
  const handleBulkActionConfirm = async () => {
    if (onBulkAction && bulkAction && selectedExpenses.size > 0) {
      await onBulkAction(Array.from(selectedExpenses), bulkAction, bulkComment);
      setSelectedExpenses(new Set());
    }
    setBulkActionDialogOpen(false);
    setBulkAction(null);
    setBulkComment('');
  };

  // Get bulk action label
  const getBulkActionLabel = (action) => t(`budget.actionLabel.${action}`, { defaultValue: action });

  // Get bulk action description
  const getBulkActionDescription = (action, count) => {
    switch (action) {
      case 'approve': return t('budget.bulkDesc.approve', { count });
      case 'reject': return t('budget.bulkDesc.reject', { count });
      case 'revert_draft': return t('budget.bulkDesc.revert_draft', { count });
      case 'mark_paid': return t('budget.bulkDesc.mark_paid', { count });
      case 'delete': return t('budget.bulkDesc.delete', { count });
      default: return '';
    }
  };

  return (
    <div className="space-y-4">
      {assetFilter && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="asset-filter-banner">
          <span className="text-sm text-blue-700">{t('budget.filteredByAsset')} <strong>{assetFilter.name}</strong></span>
          <Button variant="ghost" size="sm" className="text-blue-600 h-6 px-2" onClick={onClearAssetFilter} data-testid="btn-clear-filter">{t('budget.clearFilter')}</Button>
        </div>
      )}
      <div className="flex flex-col gap-4">
        {/* First row: Project, Status, Category filters */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-wrap gap-4">
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-[200px]" data-testid="expense-project-filter">
                <SelectValue placeholder={t('budget.filterByProject')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('budget.allProjects')}</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="expense-status-filter">
                <SelectValue placeholder={t('budget.filterByStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('budget.allStatuses')}</SelectItem>
                <SelectItem value="draft">{t('budget.draft')}</SelectItem>
                <SelectItem value="submitted">{t('budget.submitted')}</SelectItem>
                <SelectItem value="manager_approved">{t('budget.managerApproved')}</SelectItem>
                <SelectItem value="admin_approved">{t('budget.adminApproved')}</SelectItem>
                <SelectItem value="rejected">{t('budget.rejected')}</SelectItem>
                <SelectItem value="paid">{t('budget.paid')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            {setCategoryFilter && (
              <Select value={categoryFilter || 'all'} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]" data-testid="expense-category-filter">
                  <SelectValue placeholder={t('budget.filterByCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('budget.allCategories')}</SelectItem>
                  {expenseUniqueCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{getCategoryDisplayName(cat)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Payment Method Filter */}
            <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
              <SelectTrigger className="w-[240px] max-w-full" data-testid="expense-payment-method-filter">
                <SelectValue placeholder={t('budget.filterByPaymentMethod')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('budget.allPaymentMethods')}</SelectItem>
                <SelectItem value="__none__">{t('budget.noMethodSet')}</SelectItem>
                {expenseUniquePaymentMethods.map(pm => (
                  <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Employee Filter - Admin/Manager only */}
            {isAdminOrManager && setEmployeeFilter && (
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-[250px] max-w-full" data-testid="expense-employee-filter">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('budget.filterByEmployee')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('budget.allEmployees')}</SelectItem>
                  {expenseEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Asset Filter */}
            {inventoryAssets && inventoryAssets.length > 0 && (
              <Select 
                value={assetFilter ? assetFilter.id : 'all'} 
                onValueChange={(val) => {
                  if (val === 'all') {
                    onClearAssetFilter?.();
                  } else {
                    const asset = inventoryAssets.find(a => a.id === val);
                    onSetAssetFilter?.({ id: val, name: asset?.name || val });
                  }
                }}
              >
                <SelectTrigger className="w-[220px]" data-testid="expense-asset-filter">
                  <SelectValue placeholder={t('budget.filterByAsset')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('budget.allAssets')}</SelectItem>
                  {inventoryAssets.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.inventory_number ? `${a.inventory_number} - ${a.name}` : a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            {isAdminOrManager && onExport && (
              <PermButton perm="expenses.export" variant="outline" onClick={onExport} data-testid="export-expenses-btn">
                <Download className="h-4 w-4 mr-2" /> {t('budget.export')}
              </PermButton>
            )}
            <Button onClick={onAddExpense} data-testid="add-expense-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('budget.addExpense')}
            </Button>
            <Button variant="outline" onClick={onBatchEntry} data-testid="batch-entry-btn">
              <ListPlus className="h-4 w-4 mr-2" /> {t('budget.batchEntry')}
            </Button>
          </div>
        </div>

        {/* Second row: Date range filters */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{t('budget.dateRange')}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="dateFrom" className="text-sm text-gray-600">{t('budget.from2')}</Label>
            <Input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-44 h-9"
              data-testid="expense-date-from"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="dateTo" className="text-sm text-gray-600">{t('budget.to2')}</Label>
            <Input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-44 h-9"
              data-testid="expense-date-to"
            />
          </div>

          <WeekRangeFilter
            from={dateFrom}
            to={dateTo}
            onChange={(f, t2) => { setDateFrom(f); setDateTo(t2); }}
            testId="expense-week-filter"
          />
          
          {hasAnyActiveFilter && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllFilters}
              className="h-9 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="clear-all-filters-btn"
              title={t('budget.resetEveryFilterProjectStatusCategory')}
            >
              <X className="h-4 w-4 mr-1" />
              {t('budget.resetToDefaults')}
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedExpenses.size > 0 && isAdminOrManager && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-800">
              {t('budget.expensesSelected', { count: selectedExpenses.size })}
            </span>
          </div>
          <div className="flex gap-2">
            <PermButton
              perm="expenses.approve"
              variant="outline"
              size="sm"
              onClick={() => openBulkActionDialog('approve')}
              className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="bulk-approve-expenses-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" /> {t('budget.approve')}
            </PermButton>
            <PermButton
              perm="expenses.approve"
              variant="outline"
              size="sm"
              onClick={() => openBulkActionDialog('reject')}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="bulk-reject-expenses-btn"
            >
              <XCircle className="h-4 w-4 mr-1" /> {t('budget.reject')}
            </PermButton>
            <PermButton
              perm="expenses.change_status"
              variant="outline"
              size="sm"
              onClick={() => openBulkActionDialog('revert_draft')}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              data-testid="bulk-revert-expenses-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> {t('budget.revert')}
            </PermButton>
            <PermButton
              perm="expenses.mark_paid"
              variant="outline"
              size="sm"
              onClick={() => openBulkActionDialog('mark_paid')}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="bulk-markpaid-expenses-btn"
            >
              <DollarSign className="h-4 w-4 mr-1" /> {t('budget.markPaid')}
            </PermButton>
            <PermButton
              perm="expenses.delete_any"
              variant="outline"
              size="sm"
              onClick={() => openBulkActionDialog('delete')}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="bulk-delete-expenses-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {t('budget.delete')}
            </PermButton>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedExpenses(new Set())}
             data-testid="btn-clear">
              {t('budget.clear')}
            </Button>
          </div>
        </div>
      )}

      <Card>
        {isViewCustomized && (
          <div className="px-4 pt-3 flex justify-end">
            <ResetViewButton show onReset={resetSort} testId="reset-view-expenses" />
          </div>
        )}
        <CardContent className="p-0">
          <ListTotalsBar
            count={expenses.length}
            week={rangeToWeek(dateFrom, dateTo)}
            groups={[
              { key: 'amount', label: t('totals.amount'), values: sumByCurrency(expenses, (e) => e.currency, (e) => e.amount) },
              { key: 'pln', label: t('totals.inPln'), values: { PLN: expenses.reduce((s, e) => s + (Number(e.amount_pln) || 0), 0) } },
            ]}
            testId="expense-totals"
            copyable
          />
          <Table>
            <TableHeader>
              <TableRow>
                {isAdminOrManager && (
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAllExpenses}
                      aria-label={t('budget.selectAll')}
                      className={someSelected ? 'opacity-50' : ''}
                    />
                  </TableHead>
                )}
                <SortableHeader column="date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('budget.date')}</SortableHeader>
                <SortableHeader column="created_at" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('budget.created')}</SortableHeader>
                <SortableHeader column="project" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('budget.project')}</SortableHeader>
                {isAdminOrManager && <SortableHeader column="employee" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('budget.employee')}</SortableHeader>}
                <SortableHeader column="category" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('budget.category')}</SortableHeader>
                <SortableHeader column="description" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="min-w-[200px]">{t('budget.description')}</SortableHeader>
                <SortableHeader column="amount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" className="w-20">{t('budget.amount')}</SortableHeader>
                <SortableHeader column="amount_pln" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" className="w-20">{t('budget.pln')}</SortableHeader>
                <SortableHeader column="payment_method" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="w-32">{t('budget.paymentMethod')}</SortableHeader>
                <SortableHeader column="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="w-24">{t('budget.status')}</SortableHeader>
                <TableHead className="w-28">{t('budget.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdminOrManager ? 13 : 11} className="text-center py-8 text-gray-500">
                    {t('budget.noExpensesFound')}
                  </TableCell>
                </TableRow>
              ) : (
                [...visibleExpenses].sort(comparator((e, key) => {
                  switch (key) {
                    case 'date': return e.expense_date ? new Date(e.expense_date).getTime() : 0;
                    case 'created_at': return e.created_at ? new Date(e.created_at).getTime() : 0;
                    case 'project': return (e.project_name || '').toLowerCase();
                    case 'employee': return (e.assigned_employee_name || e.employee_name || '').toLowerCase();
                    case 'category': return (e.category || '').toLowerCase();
                    case 'description': return (e.description || '').toLowerCase();
                    case 'amount': return e.amount ?? 0;
                    case 'amount_pln': return e.amount_pln ?? e.amount ?? 0;
                    case 'payment_method': return (e.payment_method || '').toLowerCase();
                    case 'status': return e.status || '';
                    default: return '';
                  }
                })).map(expense => (
                  <TableRow 
                    key={expense.id}
                    className={selectedExpenses.has(expense.id) ? 'bg-blue-50' : ''}
                  >
                    {isAdminOrManager && (
                      <TableCell>
                        <Checkbox
                          checked={selectedExpenses.has(expense.id)}
                          onCheckedChange={() => toggleExpenseSelection(expense.id)}
                          aria-label={`Select expense ${expense.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <DateText value={expense.expense_date} />
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      <DateText value={expense.created_at} empty="-" />
                    </TableCell>
                    <TableCell>{getProjectName(expense.project_id)}</TableCell>
                    {isAdminOrManager && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-gray-400" />
                          <span className="text-sm">{expense.assigned_employee_name || expense.employee_name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <span>{getCategoryDisplayName(expense.category)}</span>
                      <br />
                      <span className="text-xs text-gray-500">{expense.subcategory}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm break-words whitespace-pre-wrap min-w-[200px]" data-testid={`expense-description-${expense.id}`}>{expense.description || '-'}</div>
                      {expense.vendor && (
                        <div className="text-xs text-gray-500 break-words">{expense.vendor}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      <span className="inline-flex items-center justify-end gap-0.5 whitespace-nowrap">
                        {formatCurrency(expense.amount, expense.currency)}
                        <CopyNumberButton value={expense.amount} size="sm" testId={`copy-expense-amount-${expense.id}`} />
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {expense.amount_pln ? (
                        <span className="inline-flex items-center justify-end gap-0.5 whitespace-nowrap">
                          {formatCurrency(expense.amount_pln)}
                          <CopyNumberButton value={expense.amount_pln} size="sm" testId={`copy-expense-pln-${expense.id}`} />
                        </span>
                      ) : '-'}
                      {expense.exchange_rate && expense.currency !== 'PLN' && (
                        <div className="text-xs text-gray-500">
                          {expense.exchange_rate.toFixed(2)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {expense.payment_method ? (
                        <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                          {expense.payment_method}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {getStatusBadge(expense.status)}
                        {expense.billable_to_customer && (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1" data-testid={`billable-badge-${expense.id}`}>{t('budget.billable')}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-0.5">
                        {/* Show receipt buttons - supports both legacy and new array */}
                        {(expense.receipt_file || (expense.receipts && expense.receipts.length > 0)) && (() => {
                          const receipts = expense.receipts || [];
                          const hasLostReceipts = receipts.some(r => r.storage !== 'gridfs');
                          const allLost = receipts.length > 0 && receipts.every(r => r.storage !== 'gridfs');
                          
                          return (
                            <>
                              {/* View receipts button */}
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => onViewReceipt(expense)}
                                title={allLost ? 'Receipt files may be unavailable (not in persistent storage). Click to try viewing.' : `View receipt${(receipts.length || 1) > 1 ? 's' : ''}`}
                                className={`relative h-7 w-7`}
                                data-testid={`view-receipt-${expense.id}`}
                              >
                                {allLost ? <EyeOff className="h-3.5 w-3.5 text-orange-500" /> : <Eye className="h-3.5 w-3.5" />}
                                {receipts.length > 1 && (
                                  <span className={`absolute -top-0.5 -right-0.5 text-white text-[10px] rounded-full h-3.5 w-3.5 flex items-center justify-center ${allLost ? 'bg-orange-500' : 'bg-blue-500'}`}>
                                    {receipts.length}
                                  </span>
                                )}
                              </Button>
                              {/* Download button */}
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => onDownloadReceipts(expense)}
                                title={allLost ? 'Receipt files may be unavailable' : `Download receipt${(receipts.length || 1) > 1 ? 's (ZIP)' : ''}`}
                                className={`h-7 w-7`}
                                data-testid={`download-receipt-${expense.id}`}
                              >
                                <Download className={`h-3.5 w-3.5 ${allLost ? 'text-orange-500' : 'text-green-600'}`} />
                              </Button>
                              {/* Lost receipt indicator + re-upload */}
                              {hasLostReceipts && onReuploadReceipt && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => onReuploadReceipt(expense)}
                                  title={t('budget.someReceiptsAreNotInPersistent')}
                                  className="h-7 w-7"
                                  data-testid={`reupload-receipt-${expense.id}`}
                                >
                                  <Upload className="h-3.5 w-3.5 text-orange-500" />
                                </Button>
                              )}
                            </>
                          );
                        })()}
                        
                        {/* Draft expense actions - owner can edit/submit/delete */}
                        {expense.status === 'draft' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => onEditExpense(expense)}
                              title={t('budget.editExpense')}
                              className="h-7 w-7"
                            >
                              <Edit className="h-3.5 w-3.5 text-blue-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => onSubmitExpense(expense.id)}
                              title={t('budget.submitForApproval')}
                              className="h-7 w-7"
                            >
                              <Send className="h-3.5 w-3.5" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => onDeleteExpense(expense.id)}
                              title={t('budget.deleteExpense')}
                              className="h-7 w-7"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </>
                        )}

                        {/* Copy as new — available on every status. The
                            most common use is duplicating a recurring
                            paid expense (parking, fuel) without
                            re-entering all fields. */}
                        {onCopyExpense && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onCopyExpense(expense)}
                            title={t('budget.copyAsNewExpense')}
                            className="h-7 w-7"
                            data-testid={`copy-expense-${expense.id}`}
                          >
                            <Copy className="h-3.5 w-3.5 text-slate-500" />
                          </Button>
                        )}
                        
                        {/* Approval button for submitted/manager_approved expenses */}
                        {isAdminOrManager && ['submitted', 'manager_approved'].includes(expense.status) && (
                          <PermButton
                            perm="expenses.approve"
                            variant="ghost"
                            size="icon"
                            onClick={() => onApproveExpense(expense)}
                            title={t('budget.approveExpense2')}
                            className="h-7 w-7"
                            data-testid={`approve-expense-row-${expense.id}`}
                          >
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                          </PermButton>
                        )}

                        {/* Admin dropdown menu for all statuses */}
                        {isAdminOrManager && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" title={t('budget.moreActions')} className="h-7 w-7">
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Revert to draft - for any status except draft */}
                              {expense.status !== 'draft' && (
                                <DropdownMenuItem 
                                  onClick={() => openStatusDialog(expense, 'revert_draft')}
                                  disabled={!can('expenses.change_status')}
                                  className="text-amber-600"
                                >
                                  <RotateCcw className="h-4 w-4 mr-2" />
                                  {t('budget.revertToDraft')}
                                </DropdownMenuItem>
                              )}
                              
                              {/* Reject - for submitted or manager_approved */}
                              {['submitted', 'manager_approved'].includes(expense.status) && (
                                <DropdownMenuItem 
                                  onClick={() => openStatusDialog(expense, 'reject')}
                                  disabled={!can('expenses.approve')}
                                  className="text-red-600"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  {t('budget.reject')}
                                </DropdownMenuItem>
                              )}
                              
                              {/* Mark as Paid - for admin_approved */}
                              {expense.status === 'admin_approved' && (
                                <DropdownMenuItem 
                                  onClick={() => openStatusDialog(expense, 'mark_paid')}
                                  disabled={!can('expenses.mark_paid')}
                                  className="text-green-600"
                                >
                                  <DollarSign className="h-4 w-4 mr-2" />
                                  {t('budget.markAsPaid')}
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuSeparator />
                              
                              {/* Delete - admin can delete any expense */}
                              <DropdownMenuItem 
                                onClick={() => onDeleteExpense(expense.id)}
                                disabled={!can('expenses.delete_any')}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('budget.deleteExpense2')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {visibleExpenses.length > 0 && (
              <tfoot data-testid="expenses-total-row">
                <TableRow className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                  <TableCell colSpan={isAdminOrManager ? 8 : 6} className="text-right">
                    {t('budget.totalRows', { count: visibleExpenses.length })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-testid="expenses-total-pln">
                    <span className="inline-flex items-center justify-end gap-0.5 whitespace-nowrap">
                      {formatCurrency(visibleTotalPLN, 'PLN')}
                      <CopyNumberButton value={visibleTotalPLN} size="sm" testId="copy-expenses-total-pln" />
                    </span>
                  </TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </tfoot>
            )}
          </Table>
        </CardContent>
      </Card>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getActionLabel(statusAction)}</DialogTitle>
            <DialogDescription>
              {getActionDescription(statusAction)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedExpense && (
            <div className="py-4">
              <div className="p-3 bg-gray-50 rounded-lg mb-4 space-y-1">
                <div className="text-sm">
                  <strong>{t('budget.project2')}</strong> {getProjectName(selectedExpense.project_id)}
                </div>
                <div className="text-sm">
                  <strong>{t('budget.amount2')}</strong> {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
                  {selectedExpense.amount_pln && ` (${formatCurrency(selectedExpense.amount_pln)} PLN)`}
                </div>
                <div className="text-sm">
                  <strong>{t('budget.category2')}</strong> {getCategoryDisplayName(selectedExpense.category)}
                </div>
                <div className="text-sm">
                  <strong>{t('budget.currentStatus')}</strong> {selectedExpense.status}
                </div>
              </div>
              
              {(statusAction === 'reject' || statusAction === 'revert_draft') && (
                <div className="space-y-2">
                  <Label htmlFor="statusComment">{t('budget.reasonOptional')}</Label>
                  <Textarea
                    id="statusComment"
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                    placeholder={statusAction === 'reject' ? t('budget.reasonForRejection') : t('budget.reasonForReverting')}
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} data-testid="dialog-cancel-btn">
              {t('budget.cancel')}
            </Button>
            <Button 
              onClick={handleStatusChangeConfirm}
              className={
                statusAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                statusAction === 'revert_draft' ? 'bg-amber-500 hover:bg-amber-600' :
                statusAction === 'mark_paid' ? 'bg-green-600 hover:bg-green-700' :
                ''
              }
             data-testid="dialog-statuschangeconfirm-btn">
              {getActionLabel(statusAction)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <AlertDialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {bulkAction === 'delete' && <AlertTriangle className="h-5 w-5 text-red-500" />}
              {getBulkActionLabel(bulkAction)}: {t('budget.expenseCount', { count: selectedExpenses.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {getBulkActionDescription(bulkAction, selectedExpenses.size)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {(bulkAction === 'reject' || bulkAction === 'revert_draft') && (
            <div className="py-2">
              <Label htmlFor="bulkComment">{t('budget.reasonOptional')}</Label>
              <Textarea
                id="bulkComment"
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                placeholder={bulkAction === 'reject' ? t('budget.reasonForRejection') : t('budget.reasonForReverting')}
                rows={2}
                className="mt-2"
              />
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel>{t('budget.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkActionConfirm}
              className={
                bulkAction === 'delete' || bulkAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                bulkAction === 'approve' || bulkAction === 'mark_paid' ? 'bg-green-600 hover:bg-green-700' :
                ''
              }
            >
              {getBulkActionLabel(bulkAction)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesTable;
